import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Users, Clock, FileText, MessageSquare, Tv, HeartHandshake, Calendar, ChevronDown, ChevronUp, CheckCircle, ExternalLink } from 'lucide-react';
import type { ScheduleSession } from '../../types/schedule.types';
import type { AgendaOption } from '../../types/agenda.types';
import { generateCalendarUrl } from '../../utils/calendarUtils';
import { CategoryBadge } from './CategoryBadge';

interface SessionCardProps {
  session: ScheduleSession;
  onClick: () => void;
  onMaterialsClick?: () => void;
  compact?: boolean;
  agendaOptions?: AgendaOption;
  primaryColor?: string;
  eventDate?: string;
  eventTimeZone?: string;
  eventDetails?: any; // For calendar icons
}

// Helper function to format duration in human-friendly format
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h${remainingMinutes}min`;
};

export const SessionCard = ({
  session,
  onClick,
  onMaterialsClick,
  compact = false,
  agendaOptions,
  primaryColor = '#2563eb',
  eventDate,
  eventTimeZone,
  eventDetails
}: SessionCardProps) => {
  const {
    title,
    description,
    speakers,
    room,
    type,
    image,
    time,
    endTime,
    durationMinutes,
    attachments,
    survey,
    isLivestream,
    sponsors
  } = session;
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle calendar button click
  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCalendarDropdown(!showCalendarDropdown);
  };
  // Handle specific calendar selection
  const handleAddToCalendar = (e: React.MouseEvent, type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    e.stopPropagation();
    const url = generateCalendarUrl(session, type, eventDate, eventTimeZone);
    if (type === 'apple') {
      // For Apple Calendar, we need to create a temporary link to download the .ics file
      const link = document.createElement('a');
      link.href = url;
      link.download = `${session.title.replace(/\s+/g, '-')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // For Google and Outlook, open in a new tab
      window.open(url, '_blank');
    }
    setShowCalendarDropdown(false);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCalendarDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return <div onClick={onClick} className="w-full text-left p-4 border rounded-lg hover:border-blue-300 transition-all bg-white shadow-sm hover:shadow cursor-pointer" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
      <div className="flex gap-3">
        {!compact && image && (
          <div className="flex-shrink-0 w-48 h-48 rounded-md overflow-hidden relative">
            {/* Blurred background image */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={image}
                alt=""
                className="w-full h-full object-cover blur-xl scale-110 opacity-15"
                aria-hidden="true"
              />
            </div>

            {/* Main image with white background */}
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col h-full justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {session.categories && session.categories.length > 0 && (
                  <>
                    {session.categories.slice(0, 2).map(category => (
                      <CategoryBadge key={category.id} category={category} size="sm" />
                    ))}
                    {session.categories.length > 2 && (
                      <span className="text-xs text-gray-500">
                        +{session.categories.length - 2} more
                      </span>
                    )}
                  </>
                )}
                {isLivestream && <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    <Tv size={12} />
                    LIVESTREAM
                  </span>}
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <Clock size={12}/>
                  {time} - {endTime}
                  {durationMinutes && durationMinutes > 0 && (agendaOptions?.showSessionDuration !== false) && (
                    <span className="font-medium">
                      ({formatDuration(durationMinutes)})
                    </span>
                  )}
                </span>
              </div>
              <h3 className={`font-medium ${compact ? 'line-clamp-2' : ''}`}>
                {title}
              </h3>
              {!compact && description && (agendaOptions?.showSessionDescriptions !== false) && (
                <div
                  className="text-sm text-gray-600 line-clamp-2 [&_a]:text-blue-600 [&_a:hover]:text-blue-700"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {compact && description && (agendaOptions?.showSessionDescriptions !== false) && (
                <div
                  className="text-xs text-gray-600 line-clamp-1 [&_a]:text-blue-600 [&_a:hover]:text-blue-700"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-gray-500 my-2">
              {speakers.length > 0 && (agendaOptions?.showSessionSpeakers !== false) && <div className="flex items-center gap-1">
                  <Users size={14} />
                  <span className="truncate">
                    {speakers.length === 1 ? speakers[0].name : `${speakers[0].name} +${speakers.length - 1}`}
                  </span>
                </div>}
              {room && (agendaOptions?.showSessionLocations !== false) && <div className="flex items-center gap-1">
                  <MapPin size={14} />
                  <span className="truncate">{room}</span>
                </div>}
            </div>
            {/* Session features indicators */}
            <div className="flex flex-wrap items-center justify-between mt-3">
              <div className="flex flex-wrap items-center gap-2">
                {/* Materials Button */}
                {attachments && attachments.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Use onMaterialsClick if provided, otherwise fallback to onClick
                      if (onMaterialsClick) {
                        onMaterialsClick();
                      } else {
                        onClick(); // Open the session modal where materials can be viewed
                      }
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border transition-all hover:shadow-sm"
                    style={{
                      color: primaryColor,
                      borderColor: `${primaryColor}40`,
                      backgroundColor: `${primaryColor}10`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${primaryColor}20`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                    }}
                  >
                    <FileText size={12} />
                    <span>
                      {attachments.length}{' '}
                      {attachments.length === 1 ? 'Material' : 'Materials'}
                    </span>
                  </button>
                )}
                {sponsors && sponsors.length > 0 && <div className="flex items-center gap-1 text-xs text-purple-600">
                    <HeartHandshake size={12} />
                    <span>
                      {sponsors.length}{' '}
                      {sponsors.length === 1 ? 'Sponsor' : 'Sponsors'}
                    </span>
                  </div>}
                {/* Survey Button */}
                {survey && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (survey.url && !survey.isCompleted) {
                        window.open(survey.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className={`
                      inline-flex items-center rounded-full font-medium transition-all
                      text-xs px-2 py-1 gap-1
                      relative overflow-hidden group
                      ${
                        survey.isCompleted
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                          : 'hover:shadow-sm border'
                      }
                    `}
                    style={{
                      backgroundColor: survey.isCompleted ? undefined : `${primaryColor}20`,
                      color: survey.isCompleted ? undefined : primaryColor,
                      borderColor: survey.isCompleted ? undefined : `${primaryColor}40`
                    }}
                    onMouseEnter={(e) => {
                      if (!survey.isCompleted) {
                        e.currentTarget.style.backgroundColor = `${primaryColor}30`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!survey.isCompleted) {
                        e.currentTarget.style.backgroundColor = `${primaryColor}20`;
                      }
                    }}
                    disabled={survey.isCompleted}
                  >
                    {/* Pulsing background effect when not completed */}
                    {!survey.isCompleted && (
                      <span
                        className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                        style={{ backgroundColor: primaryColor }}
                      ></span>
                    )}

                    {/* Icon */}
                    {survey.isCompleted ? (
                      <CheckCircle size={12} className="flex-shrink-0" />
                    ) : (
                      <MessageSquare size={12} className="flex-shrink-0" />
                    )}

                    {/* Text */}
                    <span>{survey.isCompleted ? 'Survey Completed' : 'Take Survey'}</span>

                    {/* External link icon */}
                    {!survey.isCompleted && survey.url && (
                      <ExternalLink size={10} className="ml-0.5" />
                    )}
                  </button>
                )}
              </div>
              {/* Calendar button */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={handleCalendarClick}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-all"
                  style={{
                    backgroundColor: `${primaryColor}15`,
                    color: primaryColor
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}25`}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}15`}
                  aria-label="Add to calendar"
                >
                  <Calendar size={12} />
                  <span className="hidden sm:inline">Add to Calendar</span>
                  {showCalendarDropdown ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {showCalendarDropdown && <div className="absolute right-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[180px]">
                    <button
                      onClick={e => handleAddToCalendar(e, 'apple')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.apple ? (
                        <img src={eventDetails.calendarIcons.apple} alt="Apple" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">🍎</span>
                      )}
                      Apple
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'google')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.google ? (
                        <img src={eventDetails.calendarIcons.google} alt="Google" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📅</span>
                      )}
                      Google <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'office365')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.office365 ? (
                        <img src={eventDetails.calendarIcons.office365} alt="Office 365" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📊</span>
                      )}
                      Office 365 <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'outlook')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.outlook ? (
                        <img src={eventDetails.calendarIcons.outlook} alt="Outlook" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📧</span>
                      )}
                      Outlook
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'outlookcom')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.outlookcom ? (
                        <img src={eventDetails.calendarIcons.outlookcom} alt="Outlook.com" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📧</span>
                      )}
                      Outlook.com <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                    <button
                      onClick={e => handleAddToCalendar(e, 'yahoo')}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                    >
                      {eventDetails?.calendarIcons?.yahoo ? (
                        <img src={eventDetails.calendarIcons.yahoo} alt="Yahoo" className="w-4 h-4" />
                      ) : (
                        <span className="w-4 h-4 text-gray-400">📮</span>
                      )}
                      Yahoo <span className="text-gray-400 text-xs">(online)</span>
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};